import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

import './HoverTooltip.css';
import './InfoTooltip.css';

const MAX_WIDTH = 260;
const VIEWPORT_MARGIN = 8;

const InfoTooltip = ({ text, label = 'More information', children, className = '' }) => {
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [pos, setPos] = useState(null);
    const wrapRef = useRef(null);

    const asWrapper = !!children;
    const open = hovered || (pinned && !asWrapper);

    const bubbleMaxWidth = asWrapper ? 460 : MAX_WIDTH;

    useLayoutEffect(() => {
        if (!open || !wrapRef.current) {
            setPos(null);
            return;
        }
        const update = () => {
            if (!wrapRef.current) return;
            const r = wrapRef.current.getBoundingClientRect();
            if (asWrapper) {
                const left = Math.min(
                    Math.max(r.left, VIEWPORT_MARGIN),
                    window.innerWidth - VIEWPORT_MARGIN - bubbleMaxWidth
                );
                setPos({ left, top: r.bottom });
            } else {
                const half = bubbleMaxWidth / 2;
                const center = Math.min(
                    Math.max(r.left + r.width / 2, VIEWPORT_MARGIN + half),
                    window.innerWidth - VIEWPORT_MARGIN - half
                );
                setPos({ left: center, top: r.top });
            }
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    useEffect(() => {
        if (!pinned) return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setPinned(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setPinned(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [pinned]);

    if (!text) return asWrapper ? <>{children}</> : null;

    const handleClick = (e) => {
        if (asWrapper) return;
        e.preventDefault();
        e.stopPropagation();
        setPinned(prev => !prev);
    };

    return (
        <span
            className={`info-tooltip${asWrapper ? ' info-tooltip--wrap' : ''}${className ? ' ' + className : ''}`}
            ref={wrapRef}
            onMouseEnter={() => {
                if (asWrapper) {
                    const el = wrapRef.current;
                    if (el && el.scrollWidth <= el.clientWidth + 1) return;
                }
                setHovered(true);
            }}
            onMouseLeave={() => setHovered(false)}
        >
            {asWrapper ? children : (
                <button
                    type="button"
                    className={`info-tooltip-button${pinned ? ' info-tooltip-button--active' : ''}`}
                    aria-label={label}
                    aria-expanded={open}
                    onClick={handleClick}
                >
                    <FontAwesomeIcon icon={faCircleInfo} />
                </button>
            )}
            {open && pos && createPortal(
                <span
                    className={`hover-tip-pop info-tooltip-bubble${asWrapper ? ' info-tooltip-bubble--inline' : ''}`}
                    role="tooltip"
                    style={{ left: pos.left, top: pos.top }}
                >
                    {text}
                </span>,
                document.body
            )}
        </span>
    );
};

export default InfoTooltip;
